// Record verb — capture the mic into an AudioBuffer.
//
// Flow: ask for getUserMedia on mount → wire an AnalyserNode off the
// mic for a live level meter → user arms + records → MediaRecorder
// collects webm/opus chunks → on stop, decode via AudioContext.
// decodeAudioData and hand off to the forge shell.
//
// Intentional choices:
//   - echoCancellation / noiseSuppression / autoGainControl all OFF.
//     Users want what they actually sampled, not a voice-call-polished
//     version of it.
//   - The level meter reads from an AnalyserNode in time-domain and
//     shows RMS; cheaper + more musically meaningful than FFT-peak.
//   - No auto-trim on silence at this layer. The WaveformPad's in/out
//     markers cover trimming cleanly; automatic trim is a separate
//     toggle added in a later polish pass.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { VerbProps } from '../types';

type Status =
  | { kind: 'idle' }
  | { kind: 'requesting' }
  | { kind: 'permission-denied'; detail: string }
  | { kind: 'armed' }
  | { kind: 'recording'; startedAt: number }
  | { kind: 'decoding' }
  | { kind: 'error'; detail: string };

const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

function pickMime(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const m of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return null;
}

export function RecordVerb({ audioContext, onBuffer, onStatus }: VerbProps) {
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [elapsedMs, setElapsedMs] = useState(0);
  const streamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  // The lib type for AnalyserNode.getFloatTimeDomainData wants
  // Float32Array<ArrayBuffer> specifically (not the widened generic).
  const analyserDataRef = useRef<Float32Array<ArrayBuffer> | null>(null);
  const levelBarRef = useRef<HTMLDivElement | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const rafRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);

  const stopLevelLoop = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const teardown = useCallback(() => {
    stopLevelLoop();
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') {
      try {
        rec.stop();
      } catch {
        // ignore — already stopping
      }
    }
    recorderRef.current = null;
    const stream = streamRef.current;
    if (stream) {
      for (const t of stream.getTracks()) t.stop();
    }
    streamRef.current = null;
    analyserRef.current?.disconnect();
    analyserRef.current = null;
    analyserDataRef.current = null;
  }, [stopLevelLoop]);

  useEffect(() => teardown, [teardown]);

  // Level meter loop: read time-domain data, compute RMS, write to a
  // DOM style directly (refs, not React state) — this fires at rAF rate.
  const startLevelLoop = useCallback(() => {
    stopLevelLoop();
    const tick = () => {
      const a = analyserRef.current;
      const data = analyserDataRef.current;
      const bar = levelBarRef.current;
      if (a && data && bar) {
        a.getFloatTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) sum += data[i]! * data[i]!;
        const rms = Math.sqrt(sum / data.length);
        const pct = Math.min(100, Math.round(rms * 250));
        bar.style.width = pct + '%';
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [stopLevelLoop]);

  const requestMic = useCallback(async () => {
    if (!audioContext) {
      setStatus({ kind: 'error', detail: 'engine not booted — press Play once' });
      return;
    }
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setStatus({ kind: 'error', detail: 'mic access not supported in this browser' });
      return;
    }
    if (!pickMime()) {
      setStatus({ kind: 'error', detail: 'MediaRecorder has no supported audio mime' });
      return;
    }
    setStatus({ kind: 'requesting' });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
      streamRef.current = stream;
      const src = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      analyserRef.current = analyser;
      // Construct via a fresh ArrayBuffer so the type system is happy
      // about the underlying buffer not being shared.
      analyserDataRef.current = new Float32Array(
        new ArrayBuffer(analyser.fftSize * 4),
      );
      src.connect(analyser);
      // Don't connect analyser to destination — we monitor visually
      // only, not audibly (to avoid feedback into the mic).
      setStatus({ kind: 'armed' });
      startLevelLoop();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setStatus({ kind: 'permission-denied', detail: msg });
    }
  }, [audioContext, startLevelLoop]);

  const startRecording = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;
    const mime = pickMime();
    if (!mime) return;
    const rec = new MediaRecorder(stream, { mimeType: mime });
    chunksRef.current = [];
    rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    rec.onerror = (e) => {
      setStatus({ kind: 'error', detail: 'MediaRecorder error: ' + String(e) });
    };
    rec.start(250); // flush chunks frequently so a crash can't lose >250ms
    recorderRef.current = rec;
    const startedAt = Date.now();
    setStatus({ kind: 'recording', startedAt });
    setElapsedMs(0);
    timerRef.current = window.setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
    }, 100);
  }, []);

  const stopRecording = useCallback(async () => {
    const rec = recorderRef.current;
    const ctx = audioContext;
    if (!rec || !ctx) return;
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setStatus({ kind: 'decoding' });
    await new Promise<void>((resolve) => {
      rec.addEventListener(
        'stop',
        () => resolve(),
        { once: true },
      );
      try {
        rec.stop();
      } catch {
        resolve();
      }
    });
    const blob = new Blob(chunksRef.current, { type: rec.mimeType });
    chunksRef.current = [];
    try {
      const buffer = await ctx.decodeAudioData(await blob.arrayBuffer());
      const suggestedName = 'rec_' + timestampTag();
      onBuffer(buffer, {
        suggestedName,
        origin: { kind: 'record', through: 'dry' },
      });
      onStatus(`Recorded ${buffer.duration.toFixed(1)}s — trim and save.`, 'ok');
      setStatus({ kind: 'idle' });
      // Release mic so the browser indicator clears.
      teardown();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setStatus({ kind: 'error', detail: 'Decode failed: ' + msg });
    }
  }, [audioContext, onBuffer, onStatus, teardown]);

  return (
    <div className="flex flex-col gap-4 min-h-[200px]">
      <StatusPanel status={status} onRequestMic={requestMic} />
      <LevelMeter barRef={levelBarRef} />
      <Controls
        status={status}
        elapsedMs={elapsedMs}
        onStart={startRecording}
        onStop={() => void stopRecording()}
      />
    </div>
  );
}

function StatusPanel({
  status,
  onRequestMic,
}: {
  status: Status;
  onRequestMic: () => void;
}) {
  if (status.kind === 'idle' || status.kind === 'requesting') {
    return (
      <div className="flex items-center justify-between bg-zinc-900 border border-zinc-800 px-4 py-3">
        <div className="text-xs text-zinc-400">
          {status.kind === 'requesting'
            ? 'Requesting mic permission…'
            : 'Mic is idle. Click Arm to request access.'}
        </div>
        <button
          type="button"
          onClick={onRequestMic}
          disabled={status.kind === 'requesting'}
          className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] bg-yellow-300 text-zinc-950 font-semibold disabled:opacity-30"
        >
          Arm
        </button>
      </div>
    );
  }
  if (status.kind === 'permission-denied') {
    return (
      <div className="bg-rose-950/40 border border-rose-800 px-4 py-3 text-xs text-rose-200">
        Mic permission denied. Check browser / OS privacy settings. ({status.detail})
      </div>
    );
  }
  if (status.kind === 'error') {
    return (
      <div className="bg-rose-950/40 border border-rose-800 px-4 py-3 text-xs text-rose-200">
        {status.detail}
      </div>
    );
  }
  if (status.kind === 'decoding') {
    return (
      <div className="bg-zinc-900 border border-zinc-800 px-4 py-3 text-xs text-zinc-300">
        Decoding take…
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between bg-zinc-900 border border-zinc-800 px-4 py-3 text-xs">
      <span className="text-zinc-300">
        {status.kind === 'armed' ? 'Mic live — armed' : 'Recording…'}
      </span>
      <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
        echoCancellation off · noiseSuppression off · autoGain off
      </span>
    </div>
  );
}

function LevelMeter({ barRef }: { barRef: React.RefObject<HTMLDivElement | null> }) {
  return (
    <div className="relative h-3 bg-zinc-950 border border-zinc-800 overflow-hidden">
      <div
        ref={barRef}
        className="absolute inset-y-0 left-0 bg-yellow-300 transition-[width] duration-75"
        style={{ width: '0%' }}
      />
    </div>
  );
}

function Controls({
  status,
  elapsedMs,
  onStart,
  onStop,
}: {
  status: Status;
  elapsedMs: number;
  onStart: () => void;
  onStop: () => void;
}) {
  const canStart = status.kind === 'armed';
  const isRecording = status.kind === 'recording';
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={onStart}
        disabled={!canStart}
        className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-rose-500 text-white font-semibold disabled:opacity-30 disabled:cursor-not-allowed"
      >
        ● Record
      </button>
      <button
        type="button"
        onClick={onStop}
        disabled={!isRecording}
        className="px-4 py-2 text-xs uppercase tracking-[0.2em] bg-zinc-800 border border-zinc-700 text-zinc-200 disabled:opacity-30"
      >
        ■ Stop
      </button>
      <div className="tabular-nums text-xs text-zinc-400">
        {formatDuration(elapsedMs)}
      </div>
    </div>
  );
}

function formatDuration(ms: number): string {
  const totalMs = Math.max(0, Math.floor(ms));
  const s = Math.floor(totalMs / 1000);
  const msPart = totalMs % 1000;
  return `${s}.${Math.floor(msPart / 100)}s`;
}

function timestampTag(): string {
  const d = new Date();
  const p = (n: number) => n.toString().padStart(2, '0');
  return (
    p(d.getHours()) + p(d.getMinutes()) + p(d.getSeconds())
  );
}
